import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router";
import {
  Settings,
  Undo,
  Redo,
  MoreHorizontal,
  Menu,
  X,
  LockKeyhole,
  Upload,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
} from "lucide-react";
import { useBasePath } from "../../contexts/BasePathContext";
import logoLight from "../../assets/logo_light.svg";
import logoDark from "../../assets/logo_dark.svg";
import { useAppContext } from "../../contexts/AppContext";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import { usePlatform } from "../../contexts/PlatformContext";
import { usePersistence } from "../../hooks/usePersistence";
import { useFiles } from "../../hooks/useFiles";
import { useTheme } from "../../hooks/useTheme";
import { useIsMobile, useIsTablet } from "../../hooks/useMobile";
import { useLicense } from "../../contexts/LicenseContext";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@exiftrator/shared/components/ui";
import { ThemeToggle } from "../ThemeToggle";
import { ViewToggle } from "./ViewToggle";
import {
  WORKSPACE_SPACING,
  getDensityClass,
} from "../Workspace/workspaceSpacing";
import { useDemo } from "../../contexts/DemoContext";

const TABLET_COLLAPSE_WIDTH = 900;

function Header({
  mobilePaneControls = null,
  mobileMenuOpen = false,
  onMobileMenuOpenChange = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { appPath } = useBasePath();
  const { files, selectedIndices } = useAppContext();
  const { undo, redo, canUndo, canRedo } = useUndoRedo();
  const { persistFiles } = usePersistence();
  const { addFiles } = useFiles();
  const { theme } = useTheme();
  const { tier } = useLicense();
  const { isDemo } = useDemo();
  const platform = usePlatform();
  const fileInputRef = useRef();
  const headerRef = useRef();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const headerInsetXClass = getDensityClass(
    isMobile,
    isTablet,
    WORKSPACE_SPACING.headerInsetX,
  );
  const headerInsetYClass = isMobile ? "py-3" : isTablet ? "py-3" : "py-4";
  const [isNarrowTablet, setIsNarrowTablet] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const isElectron = platform.name === "electron";
  const hasSelection = selectedIndices.size > 0;

  // Count modified files
  const allModifiedFiles = files.filter((f) => f.hasChanges);
  const modifiedFilesInSelection = hasSelection
    ? files.filter((f, i) => selectedIndices.has(i) && f.hasChanges)
    : [];

  const hasUnsavedChanges = allModifiedFiles.length > 0;
  const hasSelectedUnsavedChanges = modifiedFilesInSelection.length > 0;
  const isPro = tier === "pro";
  const exportPrimaryLabel = isElectron ? "Save" : "Download";
  const exportAllLabel = isElectron ? "Save all" : "Download all";
  const shouldCollapseTabletSave = isTablet && isNarrowTablet;

  const handleSaveAll = () => {
    // Pass null to save all modified files
    persistFiles(
      allModifiedFiles.map((_, i) =>
        files.findIndex((f) => f === allModifiedFiles[i]),
      ),
    );
  };

  const handleSaveSelected = () => {
    // Pass selected indices
    persistFiles(
      modifiedFilesInSelection.map((_, i) =>
        files.findIndex((f) => f === modifiedFilesInSelection[i]),
      ),
    );
  };

  const handleFileSelect = (e) => {
    addFiles(e.target.files);
  };

  const handleSelect = () => {
    if (isDemo) {
      navigate("/app");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleUndo = () => {
    const description = undo();
    if (description) {
      console.log("Undone:", description);
    }
  };

  const handleRedo = () => {
    const description = redo();
    if (description) {
      console.log("Redone:", description);
    }
  };

  const closeMobileMenu = () => {
    if (onMobileMenuOpenChange) {
      onMobileMenuOpenChange(false);
    }
  };

  const openMobileMenu = () => {
    if (onMobileMenuOpenChange) {
      onMobileMenuOpenChange(true);
    }
  };

  const toggleMobileMenu = () => {
    if (mobileMenuOpen) {
      closeMobileMenu();
      return;
    }
    openMobileMenu();
  };

  const runMobileAction = (action) => {
    action();
    closeMobileMenu();
  };

  const nonMobileActionConfig = useMemo(
    () => ({
      inline: [
        { id: "add", visible: true },
        { id: "save", visible: !shouldCollapseTabletSave },
        { id: "undo", visible: true },
        { id: "redo", visible: true },
        { id: "preferences", visible: !isTablet },
        { id: "theme", visible: !isTablet },
        { id: "unlockPro", visible: !isTablet && !isPro },
      ],
      overflow: [
        { id: "save", visible: shouldCollapseTabletSave },
        { id: "preferences", visible: isTablet },
        { id: "theme", visible: isTablet },
        { id: "unlockPro", visible: isTablet && !isPro },
      ],
    }),
    [isPro, isTablet, shouldCollapseTabletSave],
  );

  useEffect(() => {
    if (onMobileMenuOpenChange) {
      onMobileMenuOpenChange(false);
    }
  }, [location.pathname, onMobileMenuOpenChange]);

  useEffect(() => {
    const mql = window.matchMedia(
      `(min-width: 768px) and (max-width: ${TABLET_COLLAPSE_WIDTH - 1}px)`,
    );
    const onChange = () => {
      setIsNarrowTablet(mql.matches);
    };

    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (isMobile) return;
    if (onMobileMenuOpenChange) {
      onMobileMenuOpenChange(false);
    }
  }, [isMobile, onMobileMenuOpenChange]);

  useEffect(() => {
    if (!isMobile || !mobileMenuOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (onMobileMenuOpenChange) {
          onMobileMenuOpenChange(false);
        }
      }
    };

    const handlePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (headerRef.current?.contains(target)) return;
      if (onMobileMenuOpenChange) {
        onMobileMenuOpenChange(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("pointerdown", handlePointerDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [isMobile, mobileMenuOpen, onMobileMenuOpenChange]);

  const renderInlineAction = (id) => {
    if (id === "add") {
      return (
        <Button
          key={id}
          variant="default"
          size="default"
          onClick={handleSelect}
        >
          <Upload data-icon="inline-start" className="size-4" /> Select
        </Button>
      );
    }

    if (id === "save") {
      return (
        <Button
          key={id}
          variant="outline"
          onClick={handleSaveAll}
          disabled={!hasUnsavedChanges}
          title={exportAllLabel}
        >
          {exportPrimaryLabel}
        </Button>
      );
    }

    if (id === "undo") {
      return (
        <Button
          key={id}
          aria-label="Undo"
          variant="ghost"
          size="icon"
          onClick={handleUndo}
          disabled={!canUndo}
          title="Undo (Cmd+Z)"
        >
          <Undo data-icon="inline-start" className="size-4" />
        </Button>
      );
    }

    if (id === "redo") {
      return (
        <Button
          key={id}
          aria-label="Redo"
          variant="ghost"
          size="icon"
          onClick={handleRedo}
          disabled={!canRedo}
          title="Redo (Cmd+Shift+Z)"
        >
          <Redo data-icon="inline-start" className="size-4" />
        </Button>
      );
    }

    if (id === "preferences") {
      return (
        <Button
          key={id}
          variant="ghost"
          size="icon"
          onClick={() => navigate(appPath("/settings"))}
          aria-label="Preferences"
          title="Preferences"
        >
          <Settings data-icon="inline-start" />
        </Button>
      );
    }

    if (id === "theme") {
      return <ThemeToggle key={id} />;
    }

    if (id === "unlockPro") {
      return (
        <Button
          key={id}
          variant="outline"
          size="default"
          onClick={() => navigate(appPath("/settings/license"))}
          title="Unlock Pro"
        >
          <LockKeyhole data-icon="inline-start" className="size-3.5" />
          Unlock Pro
        </Button>
      );
    }

    return null;
  };

  const renderOverflowAction = (id) => {
    if (id === "save") {
      return (
        <DropdownMenuItem
          key={id}
          onClick={handleSaveAll}
          disabled={!hasUnsavedChanges}
        >
          {exportAllLabel}
        </DropdownMenuItem>
      );
    }

    if (id === "preferences") {
      return (
        <DropdownMenuItem
          key={id}
          onClick={() => navigate(appPath("/settings"))}
        >
          <Settings className="mr-2 size-4" />
          Preferences
        </DropdownMenuItem>
      );
    }

    if (id === "theme") {
      return (
        <DropdownMenuItem key={id} onSelect={(e) => e.preventDefault()}>
          <ThemeToggle />
        </DropdownMenuItem>
      );
    }

    if (id === "unlockPro") {
      return (
        <DropdownMenuItem
          key={id}
          onClick={() => navigate(appPath("/settings/license"))}
        >
          <LockKeyhole className="mr-2 size-4" />
          Unlock Pro
        </DropdownMenuItem>
      );
    }

    return null;
  };

  return (
    <header ref={headerRef} className="bg-bg-secondary border-b border-border-default shadow-sm">
      <div
        className={`flex items-center justify-between ${headerInsetXClass} ${headerInsetYClass}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />
        <div className="flex items-center gap-2">
          <Link to="/" className="flex items-center">
            <img
              src={theme === "dark" ? logoDark : logoLight}
              alt="Image Metadata Editor Logo"
              className={`w-auto ${isMobile ? "h-5" : "h-7"}`}
            />
          </Link>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-1.5"
            onClick={() => navigate(appPath("/settings/license"))}
            title="Open license settings"
          >
            <Badge variant={isPro ? "accent" : "secondary"} size="sm">
              {isPro ? "PRO" : "FREE"}
            </Badge>
          </Button>
        </div>
        {files.length > 0 && !isMobile && (
          <div className="flex items-center gap-4">
            {/* View Mode Toggle */}
            <ViewToggle />
          </div>
        )}

        <div className="flex shrink-0 gap-1 sm:gap-2 items-center">
          {/* Mobile uses inline menu; non-mobile uses grouped controls */}
          {isMobile ? (
            <>
              <ThemeToggle />
              <Button
                variant="ghost"
                size="icon"
                className="size-11"
                aria-label={mobileMenuOpen ? "Close app menu" : "Open app menu"}
                aria-expanded={mobileMenuOpen}
                aria-controls="app-mobile-nav-menu"
                onClick={toggleMobileMenu}
              >
                {mobileMenuOpen ? (
                  <X className="size-5" />
                ) : (
                  <Menu className="size-5" />
                )}
              </Button>
            </>
          ) : (
            <>
              {nonMobileActionConfig.inline
                .filter((action) => action.visible)
                .map((action) => renderInlineAction(action.id))}

              {nonMobileActionConfig.overflow.some((action) => action.visible) && (
                <DropdownMenu open={overflowOpen} onOpenChange={setOverflowOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label="More options">
                      <MoreHorizontal className="size-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {nonMobileActionConfig.overflow
                      .filter((action) => action.visible)
                      .map((action) => renderOverflowAction(action.id))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          )}
        </div>
      </div>

      {isMobile && mobileMenuOpen && (
        <div
          id="app-mobile-nav-menu"
          aria-label="App mobile navigation menu"
          className="border-t border-border-subtle/50 bg-bg-deep/95 px-3 py-3 backdrop-blur-xl"
        >
          <div className="flex flex-col gap-2">
            {!isPro && (
              <Button
                variant="outline"
                className="h-11 w-full justify-start"
                onClick={() =>
                  runMobileAction(() => navigate(appPath("/settings/license")))
                }
                title="Unlock Pro"
              >
                <LockKeyhole data-icon="inline-start" className="size-3.5" />
                Unlock Pro
              </Button>
            )}

            <Button
              variant="default"
              className="h-11 w-full justify-start"
              onClick={() => runMobileAction(handleSelect)}
            >
              <Upload data-icon="inline-start" className="size-4" />
              Select
            </Button>

            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="ghost"
                className="h-11 justify-start"
                onClick={() => runMobileAction(handleUndo)}
                disabled={!canUndo}
                title="Undo (Cmd+Z)"
              >
                <Undo data-icon="inline-start" className="size-4" />
                Undo (Cmd+Z)
              </Button>
              <Button
                variant="ghost"
                className="h-11 justify-start"
                onClick={() => runMobileAction(handleRedo)}
                disabled={!canRedo}
                title="Redo (Cmd+Shift+Z)"
              >
                <Redo data-icon="inline-start" className="size-4" />
                Redo (Cmd+Shift+Z)
              </Button>
            </div>

            {mobilePaneControls && (
              <>
                <Button
                  variant="ghost"
                  className="h-11 w-full justify-start"
                  onClick={() =>
                    runMobileAction(mobilePaneControls.onToggleLeftPane)
                  }
                >
                  {mobilePaneControls.leftPaneOpen ? (
                    <PanelLeftClose data-icon="inline-start" className="size-4" />
                  ) : (
                    <PanelLeftOpen data-icon="inline-start" className="size-4" />
                  )}
                  {mobilePaneControls.leftPaneOpen
                    ? `Hide ${mobilePaneControls.leftPaneLabel}`
                    : `Show ${mobilePaneControls.leftPaneLabel}`}
                </Button>
                <Button
                  variant="ghost"
                  className="h-11 w-full justify-start"
                  onClick={() =>
                    runMobileAction(mobilePaneControls.onToggleRightPane)
                  }
                >
                  {mobilePaneControls.rightPaneOpen ? (
                    <PanelRightClose data-icon="inline-start" className="size-4" />
                  ) : (
                    <PanelRightOpen data-icon="inline-start" className="size-4" />
                  )}
                  {mobilePaneControls.rightPaneOpen
                    ? `Hide ${mobilePaneControls.rightPaneLabel}`
                    : `Show ${mobilePaneControls.rightPaneLabel}`}
                </Button>
              </>
            )}

            <Button
              variant="ghost"
              className="h-11 w-full justify-start"
              onClick={() => runMobileAction(() => navigate(appPath("/settings")))}
            >
              <Settings data-icon="inline-start" className="size-4" />
              Preferences
            </Button>

            <Button
              variant="ghost"
              className="h-11 w-full justify-start"
              onClick={() => runMobileAction(handleSaveAll)}
              disabled={!hasUnsavedChanges}
            >
              {isElectron ? "Save all" : "Download all"}
            </Button>
            <Button
              variant="ghost"
              className="h-11 w-full justify-start"
              onClick={() => runMobileAction(handleSaveSelected)}
              disabled={!hasSelectedUnsavedChanges}
            >
              {isElectron ? "Save selected" : "Download selected"}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
